import type { GeoJSON, Geometry } from "geojson";


const ENDPOINT = "https://nominatim.openstreetmap.org";

export interface SearchOptions {
  limit?: number;
  address_details?: boolean;
  name_details?: boolean;
  extra_tags?: boolean;
  extrances?: boolean;
  dedupe?: boolean;
  polygon?: boolean;
  restrictions?: SearchRestrictions;
}

export interface SearchRestrictions {
  /// ISO 3166-1alpha2
  countries?: string[];
  layers?: ("address" | "poi" | "railway" | "nature" | "manmade")[];
  feature?: "country" | "state" | "city" | "settlement" | "manmade";
  viewbox?: [LongLat, LongLat];
  viewbox_bounded?: boolean;
}

export interface LongLat {
  longitude: number;
  latitude: number;
}

function rot13(str: string): string {
  return str.replace(/[a-zA-Z]/g, (char) => {
    const code = char.charCodeAt(0);
    const shift = code <= 90 ? 65 : 97;
    return String.fromCharCode(((code - shift + 13) % 26) + shift);
  });
}

function build_search_params(options?: SearchOptions): URLSearchParams {
  const params = new URLSearchParams();
  params.set("format", "geojson");
  params.set("email", rot13("uryc#inkkvar;pbz").replace("#", "@").replace(";", "."));

  if (options?.limit != null) {
    params.set("limit", Math.floor(options.limit).toString());
  }
  params.set("addressdetails", options?.address_details ? "1" : "0");
  params.set("namedetails", options?.name_details ? "1" : "0");
  params.set("extratags", options?.extra_tags ? "1" : "0");
  params.set("extrances", options?.extrances ? "1" : "0");
  params.set("dedupe", options?.dedupe === false ? "0" : "1");
  if (options?.polygon) {
    params.set("polygon_geojson", "1");
  }

  if (options?.restrictions != null) {
    if (options.restrictions?.countries != null) {
      params.set("countrycodes", options.restrictions.countries.join(","))
    }
    if (options.restrictions?.layers != null) {
      params.set("layer", options.restrictions.layers.join(","))
    }
    if (options.restrictions?.feature != null) {
      params.set("featureType", options.restrictions.feature);
    }
    if (options.restrictions?.viewbox != null) {
      const box = options.restrictions.viewbox;
      params.set(
        "viewbox",
        [box[0].longitude, box[0].latitude, box[1].longitude, box[1].latitude].join(",")
      );

      if (options.restrictions?.viewbox_bounded) {
        params.set("bounded", "1");
      }
    }
  }

  return params;
}

export interface StructuredQuery {
  // Name and/or type of POI
  amenity?: string;
  // Housenumber and streetname
  street?: string;
  // City
  city?: string;
  // County
  county?: string;
  // State
  state?: string;
  // Country
  country?: string;
  // Postal code
  postalcode?: string;
}

// Either under the main object key or keys of the place tag.
type AddressDetailKeys = "continent" | "country" | "country_code" | "region" | "state" | "state_district" | "county" | "ISO3166-2-lvl" | "municipality" | "city" | "town" | "village" | "city_district" | "district" | "borough" | "suburb" | "subdivision" | "hamlet" | "croft" | "isolated_dwelling" | "neighbourhood" | "allotments" | "quarter" | "city_block" | "residential" | "farm" | "farmyard" | "industrial" | "commercial" | "retail" | "road" | "house_number" | "house_name" | "emergency" | "historic" | "military" | "natural" | "landuse" | "place" | "railway" | "man_made" | "aerialway" | "boundary" | "amenity" | "aeroway" | "club" | "craft" | "leisure" | "office" | "mountain_pass" | "shop" | "tourism" | "bridge" | "tunnel" | "waterway" | "postcode";

export interface ResultGeoProperties {
  /// Reference to the Nominatim internal database ID.
  /// Note: this is not a persistent identifier!
  place_id: number;
  /// Reference to the OSM object
  osm_id: number;
  /// OSM object type
  osm_type: "node" | "way" | "relation";

  /// OSM object category. Represented as category=type.
  /// https://wiki.openstreetmap.org/wiki/OpenStreetBrowser/Category_list
  category: string;
  /// OSM object category value. Represented as category=type.
  /// https://wiki.openstreetmap.org/wiki/OpenStreetBrowser/Category_list
  type: string;

  /// Full comma-separated address
  display_name: string;

  /// Class search rank
  place_rank: number;
  // Computed importance rank
  importance: number;

  /// Link to class icon (if available)
  icon?: string;

  // Address details (only with address_details)
  address?: Record<AddressDetailKeys, string>;

  // Additional useful tags (only with extra_tags)
  extratags?: Record<string, string>;

  // Full list of available names (only with name_details)
  namedetails?: Record<string, string>;

  // Objects representing tagged entrances for the object, or null if none (only with entrances)
  entrances?: Entrance[];
}

export interface Entrance {
  /// Reference to the OSM object
  osm_id: number;

  /// OSM object entrance value. Represented as entrance=type.
  /// https://wiki.openstreetmap.org/wiki/OpenStreetBrowser/Category_list
  type: string;

  // Additional useful tags (only with extra_tags)
  extratags?: Record<string, string | number>;
}

export async function search(query: string | StructuredQuery, options?: SearchOptions, fetchOptions?: RequestInit): Promise<GeoJSON<Geometry, ResultGeoProperties>> {
  const params = build_search_params(options);
  if (typeof query === "string") {
    params.set("q", query);
  } else {
    if (query.amenity != null) { params.set("amenity", query.amenity); }
    if (query.street != null) { params.set("street", query.street); }
    if (query.city != null) { params.set("city", query.city); }
    if (query.county != null) { params.set("county", query.county); }
    if (query.state != null) { params.set("state", query.state); }
    if (query.country != null) { params.set("country", query.country); }
    if (query.postalcode != null) { params.set("postalcode", query.postalcode); }
  }

  const json = await (await fetch(`${ENDPOINT}/search?${params.toString()}`, {
    referrerPolicy: "origin",
    cache: "force-cache",
    ...fetchOptions,
  })).json();
  return json as GeoJSON<Geometry, ResultGeoProperties>;
}
